import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RecommendationsTriggeredBy } from '@kouto/types';
import { useBrandId } from 'AppProvider';
import useExperienceId from 'hooks/use-experience-id';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';
import { useRecommendationsViewEvent } from 'features/analytics/hooks/useRecommendationsViewEvent';
import generateListingRecommendations from 'api/listingRecommendation/generateListingRecommendations';
import useGetListingRecommendations from 'api/listingRecommendation/useGetListingRecommendations';
import { GetListingRecommendationsResponse } from 'api/listingRecommendation/getListingRecommendations';
import { FeaturedListingV3 } from 'types/featuredListingV3';
import { BookingConfirmationCard } from 'components/molecules/BookingConfirmationCard';
import { BookingConfirmationCardSkeleton } from 'components/molecules/BookingConfirmationCard/BookingConfirmationCardSkeleton';
import { NotFoundExperienceMessage } from 'components/molecules/NotFoundExperienceMessage';
import { RecommendationsHeading } from 'components/molecules/RecommendationsHeading';
import Link from 'components/Link';
import { RecommendationCardGrid } from 'components/molecules/RecommendationCardGrid';
import {
  ExploreAllButton,
  Section,
} from 'components/organisms/DeadLinkRecovery/DeadLinkRecovery.styled';

type RecommendedListing = GetListingRecommendationsResponse['listings'][number];

const toCardListing = (
  listing: RecommendedListing,
  brandId: string,
): FeaturedListingV3 => ({
  ...listing,
  category: listing.category ? { ...listing.category, brandId } : null,
  isUnlisted: listing.isUnlisted ?? false,
  vibes: listing.vibes.map((vibe) => ({ ...vibe, brandId })),
});

// Generate ranks dead links in the request, so the GET that follows is already complete and the
// poll stops after its first answer. Same two calls as the upsell screens.
const DeadLinkRecovery = () => {
  const { t: translate } = useTranslation();
  const brandId = useBrandId();
  const sourceExperienceSlug = useExperienceId();
  const languageCode = useSupportedLanguage();

  const [recommendationId, setRecommendationId] = useState<string | null>(null);
  const [hasGenerateFailed, setHasGenerateFailed] = useState(false);

  useEffect(() => {
    // Ignore a response that lands after the page has moved on.
    const request = { isCancelled: false };

    if (brandId) {
      generateListingRecommendations(brandId, {
        brandId,
        experienceIds: [],
        language: languageCode,
        resourceGroupCollectionIds: [],
        sourceExperienceSlug,
        triggeredBy: RecommendationsTriggeredBy.DEAD_LINK,
      })
        .then((response) => {
          if (!request.isCancelled) {
            setRecommendationId(response.recommendationId);
          }
        })
        .catch(() => {
          if (!request.isCancelled) {
            setHasGenerateFailed(true);
          }
        });
    }

    return () => {
      request.isCancelled = true;
    };
  }, [brandId, sourceExperienceSlug, languageCode]);

  const { listings, isCompleted, isFailed } = useGetListingRecommendations({
    language: languageCode,
    maxPolls: 5,
    pollInterval: 1000,
    recommendationId,
  });

  const isLoading = !recommendationId || (!isCompleted && !isFailed);

  useRecommendationsViewEvent({
    listings,
    isShown: !isLoading && !isFailed && listings.length > 0,
  });

  if (
    !brandId ||
    hasGenerateFailed ||
    (!isLoading && (isFailed || listings.length === 0))
  ) {
    // The plain not-found screen: nothing qualifies or the request failed.
    return <NotFoundExperienceMessage />;
  }

  return (
    <Section>
      <RecommendationsHeading
        title={translate('notFoundExperienceTitle')}
        subtitle={translate('deadLinkRecoverySubtitle')}
      />
      <RecommendationCardGrid>
        {isLoading ? (
          <>
            <BookingConfirmationCardSkeleton />
            <BookingConfirmationCardSkeleton />
            <BookingConfirmationCardSkeleton />
          </>
        ) : (
          listings.map((listing, index) => (
            <BookingConfirmationCard
              key={listing.id}
              listing={toCardListing(listing, brandId)}
              recommendationId={recommendationId}
              index={index}
            />
          ))
        )}
      </RecommendationCardGrid>
      <ExploreAllButton as={Link} to="/e">
        {translate('viewAllExperiences')}
      </ExploreAllButton>
    </Section>
  );
};

export default DeadLinkRecovery;
